import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getRedemptions } from '@/api/member';
import type { RedemptionRecord } from '@shared/api.interface';
import { Copy, ExternalLink, History, Loader2, Search, Ticket } from 'lucide-react';
import { showUserError, showUserSuccess } from '@/utils/user-toast';
import { extractFirstUrl } from '@/utils/url';

interface MemberHistoryDialogProps {
  passcode: string;
  triggerClassName?: string;
  /** 受控打开：从“我的会员”面板或卡片“查看资源”打开时使用 */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** 不渲染自带的触发按钮 */
  hideTrigger?: boolean;
  /** 打开时预填的搜索词（例如从某个已领取主题点进来） */
  initialQuery?: string;
}

const PAGE_SIZE = 10;

function formatDateTime(value: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function MemberHistoryDialog({
  passcode,
  triggerClassName,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
  initialQuery = '',
}: MemberHistoryDialogProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [records, setRecords] = useState<RedemptionRecord[]>([]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    async function loadRecords() {
      setLoading(true);
      try {
        const result = await getRedemptions({ passcode, pageSize: 500 });
        if (!cancelled) {
          setRecords(result.items);
        }
      } catch (error) {
        if (!cancelled) {
          setRecords([]);
          showUserError('领取记录加载失败，请稍后重试');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadRecords();

    return () => {
      cancelled = true;
    };
  }, [open, passcode]);

  useEffect(() => {
    if (open) {
      setQuery(initialQuery);
    }
    setPage(1);
  }, [open, initialQuery]);

  const normalizedQuery = query.trim().toLowerCase();
  const allVisibleRecords = records.filter(
    (record) => record.record_type === 'quota_adjustment' || record.result === 'success'
  );
  const visibleRecords = normalizedQuery
    ? allVisibleRecords.filter((record) =>
        `${record.topic_name || ''} ${record.adjustment_note || ''}`.toLowerCase().includes(normalizedQuery)
      )
    : allVisibleRecords;
  const totalPages = Math.max(1, Math.ceil(visibleRecords.length / PAGE_SIZE));
  const paginatedRecords = visibleRecords.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleCopy = async (downloadLink: string) => {
    try {
      await navigator.clipboard.writeText(downloadLink);
      showUserSuccess('已复制到剪贴板');
    } catch {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = downloadLink;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        showUserSuccess('已复制到剪贴板');
      } catch {
        showUserError('复制失败，请手动复制');
      }
    }
  };

  const handleOpen = (content: string) => {
    const url = extractFirstUrl(content);
    if (!url) {
      showUserError('没有找到可直接打开的链接，请复制后到网盘 App 打开');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      {!hideTrigger && (
        <Button variant="outline" size="sm" className={triggerClassName} onClick={() => setOpen(true)}>
          <History className="mr-1 h-4 w-4" />
          领取记录
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="h-[min(90dvh,48rem)] overflow-hidden sm:h-auto sm:max-h-[90vh] sm:max-w-4xl"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateRows: 'auto minmax(0, 1fr) auto',
            gap: 0,
            padding: 0,
          }}
        >
          <DialogHeader className="space-y-3 border-b bg-card px-4 pb-3 pt-4 text-left sm:px-6 sm:pb-4 sm:pt-6">
            <div className="space-y-1 pr-8">
              <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <History className="h-5 w-5 text-cta" />
                我的领取记录
              </DialogTitle>
              <DialogDescription>展示成功领取记录和管理员额度调整记录，方便你核对会员权益。</DialogDescription>
            </div>
            {allVisibleRecords.length > 0 && (
              <label className="flex h-11 items-center gap-2 rounded-xl border border-border bg-background px-3 focus-within:border-cta focus-within:ring-2 focus-within:ring-cta/20">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="sr-only">搜索领取记录</span>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(1);
                  }}
                  placeholder={`在 ${allVisibleRecords.length} 条记录里搜主题`}
                  className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground sm:text-sm"
                />
              </label>
            )}
          </DialogHeader>

          <div className="min-h-0 w-full min-w-0 overflow-hidden">
            <div className="h-full w-full min-w-0 overflow-y-auto overflow-x-hidden">
              <div className="min-w-0 max-w-full space-y-2 px-3 py-3 sm:space-y-3 sm:px-6 sm:py-5">
                {loading ? (
                  <div className="flex items-center justify-center py-12 text-muted-foreground">
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    领取记录加载中...
                  </div>
                ) : visibleRecords.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center text-sm text-muted-foreground">
                      {normalizedQuery ? `没有找到「${query.trim()}」相关的记录` : '暂无领取或额度调整记录'}
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {paginatedRecords.map((record) => (
                      <Card key={record.id} className="min-w-0 max-w-full overflow-hidden border-border/70">
                        <CardContent className="flex min-w-0 w-full max-w-full flex-col gap-2.5 overflow-hidden p-3 sm:p-4">
                          <div className="flex min-w-0 w-full max-w-full flex-col gap-2.5 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex min-w-0 w-full max-w-full flex-1 items-center gap-2">
                              <Ticket className="h-4 w-4 shrink-0 text-primary" />
                              <p className="min-w-0 flex-1 truncate font-medium text-foreground">{record.topic_name}</p>
                              <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground sm:text-sm">
                                {formatDateTime(record.redemption_time)}
                              </span>
                            </div>
                            <Badge
                              variant="secondary"
                              className={`shrink-0 ${
                                record.record_type === 'quota_adjustment'
                                  ? 'bg-sky-50 text-sky-800'
                                  : 'bg-claimed-soft text-claimed'
                              }`}
                            >
                              {record.record_type === 'quota_adjustment' ? '管理员调整' : '领取成功'}
                            </Badge>
                          </div>

                          {record.record_type === 'quota_adjustment' ? (
                            <div className="min-w-0 max-w-full overflow-hidden space-y-1.5 rounded-lg border bg-muted/40 p-3 text-sm">
                              <p className="text-muted-foreground">额度变化</p>
                              <p className="wrap-anywhere">
                                本周期：{record.quota_before?.cycle_used ?? 0} → {record.quota_after?.cycle_used ?? 0}；
                                今日：{record.quota_before?.today_used ?? 0} → {record.quota_after?.today_used ?? 0}；
                                累计：{record.quota_before?.total_redemptions ?? 0} → {record.quota_after?.total_redemptions ?? 0}
                              </p>
                              {(record.quota_before?.next_cycle_borrowed ?? 0) !== (record.quota_after?.next_cycle_borrowed ?? 0) && (
                                <p className="wrap-anywhere">
                                  预支下周期：{record.quota_before?.next_cycle_borrowed ?? 0} → {record.quota_after?.next_cycle_borrowed ?? 0} 次
                                </p>
                              )}
                              <p className="wrap-anywhere text-muted-foreground">
                                调整说明：{record.adjustment_note || '未填写调整说明'}
                              </p>
                            </div>
                          ) : record.download_link ? (
                            <div className="min-w-0 max-w-full overflow-hidden rounded-lg border bg-muted/40 p-3">
                              <p className="text-xs text-muted-foreground">领取资源内容</p>
                              <div className="mt-2 flex min-w-0 w-full max-w-full flex-col gap-2 sm:flex-row">
                                <div className="max-h-28 min-h-10 min-w-0 max-w-full flex-1 overflow-y-auto overflow-x-hidden rounded border bg-white px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap wrap-anywhere">
                                  {record.download_link}
                                </div>
                                <div className="grid min-w-0 max-w-full shrink-0 grid-cols-2 gap-2 sm:flex sm:flex-col">
                                  <Button
                                    className="h-10 shrink-0 bg-cta text-cta-foreground hover:bg-cta-hover"
                                    aria-label="复制领取内容"
                                    onClick={() => handleCopy(record.download_link!)}
                                  >
                                    <Copy className="h-4 w-4" />
                                    复制
                                  </Button>
                                  {extractFirstUrl(record.download_link) && (
                                    <Button
                                      variant="outline"
                                      className="h-10 shrink-0"
                                      aria-label="打开资源链接"
                                      onClick={() => handleOpen(record.download_link!)}
                                    >
                                      <ExternalLink className="h-4 w-4" />
                                      打开
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </CardContent>
                      </Card>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* 固定底部分页区：记录再多也始终可达 */}
          {!loading && visibleRecords.length > 0 && (
            <div className="shrink-0 border-t bg-white px-4 py-3 sm:px-6">
              <div className="flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
                <span>共 {visibleRecords.length} 条记录，第 {page}/{totalPages} 页</span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 sm:flex-none"
                    disabled={page <= 1}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    上一页
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 sm:flex-none"
                    disabled={page >= totalPages}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    下一页
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
